<template>
  <div class="laskuri-container text-left">
    <div class="row mb-3">
      <div class="col-md-6 laskuri-column">
        <div class="row laskuri-input laskuri-osoite-input">
          <div class="col-12">
            <label for="text">Tuotteen toimitusosoite</label>
            <input
              ref="osoite"
              class="form-control"
              type="text"
              placeholder=""
            />
            <span v-if="error" class="error-message"
              >Toimitusosoite on pakollinen</span
            >
          </div>
        </div>
        <div class="row laskuri-input laskuri-multa-input">
          <div class="col-12">
            <label for="multavuus">Multavuus</label>
            <select
              id="multavuus"
              v-model="selectedMultavuus"
              :disabled="selectedMaalaji && selectedMaalaji.length < 2"
              class="form-select"
              @change="
                setSelectedAmounts({
                  selectedMultavuus: parseInt($event.target.value),
                })
              "
            >
              <option
                v-for="(multavuus, index) in multavuudet"
                :key="index"
                :value="multavuus.value"
              >
                {{ multavuus.text }}
              </option>
            </select>
          </div>
        </div>
        <div class="row laskuri-input laskuri-maalaji-input">
          <div class="col-12">
            <label for="maalajit">Maalajit</label>
            <select
              id="maalajit"
              v-model="selectedMaalaji"
              class="form-select"
              @change="setSelectedMaalaji($event)"
            >
              <option
                v-for="(maalaji, index) in maalajit"
                :id="index"
                :key="index"
                :value="maalaji.value"
              >
                {{ maalaji.text }}
              </option>
            </select>
          </div>
        </div>
      </div>
      <div class="col-md-6 laskuri-column">
        <div class="row laskuri-input laskuri-area-input">
          <div class="col-12">
            <label for="text">Lohkon koko (ha)</label>
            <input
              v-model="area"
              class="form-control"
              type="text"
              placeholder=""
              @change="
                setSelectedAmounts({
                  area: parseInt($event.target.value),
                })
              "
            />
          </div>
        </div>
        <div class="row laskuri-input laskuri-ph-input">
          <div class="col-6">
            <div class="narrow-input-wrapper-left">
              <label for="text">Lähtö pH</label>
              <input
                v-model="phStart"
                type="number"
                class="form-control"
                placeholder=""
                @change="
                  setSelectedAmounts({
                    phStart: parseFloat($event.target.value),
                  })
                "
              />
            </div>
          </div>
          <div class="col-6">
            <div class="narrow-input-wrapper-right">
              <label for="text">Tavoite pH</label>
              <input
                v-model="phTarget"
                class="form-control"
                type="number"
                placeholder=""
                @change="
                  setSelectedAmounts({
                    phTarget: parseFloat($event.target.value),
                  })
                "
              />
            </div>
          </div>
        </div>
        <div class="row laskuri-input laskuri-submit-input">
          <div class="show-button col-12">
            <button
              :disabled="loading"
              class="btn btn-primary no-arrow"
              @click="handleSubmit"
            >
              <i v-if="loading" class="fal fa-spinner-third loading-icon"></i>
              {{ getKalkkilaskuriProducts ? "Päivitä" : "Näytä" }} tuotteet
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <p class="info-text">
    Laskuri käyttää vertailulaskennassa kalkkien nopeaa neutralointikykyä, sillä
    se kertoo luotettavimmin, miten nopeasti saavutetaan haluttu pH:n nosto.
    Nopea neutralointikyky on yleisesti hyväksytty tapa arvioida kalkkien
    neutralointivaikutusta. Samaa vertailua käyttää myös Käytännön Maamiehen
    kalkkitaulukko.
  </p>
</template>

<script>
/* eslint  no-undef: 0 */
import { mapActions, mapGetters, mapMutations, mapState, useStore } from "vuex";
import { onMounted, ref } from "vue";
import { get, set, unrefElement } from "@vueuse/core";
import { enableEnterKey, isInRange } from "@/shared/helpers";
import gmaps from "@/shared/gmaps";
import {
  createProductObject,
  getDirectionDistance,
  retrieveLocation,
} from "../../shared/helpers";
import {
  FETCH_PRODUCTS,
  SET_FILTERED_PRODUCTS,
  SET_SELECTED_AMOUNTS,
  UPDATE_PROFILE,
} from "../../store/constants";

export default {
  name: "Laskuri",
  setup() {
    const store = useStore();

    const osoite = ref(null);

    const autocomplete = ref(null);

    let google;

    onMounted(async () => {
      google = await gmaps();

      const options = {
        componentRestrictions: { country: "fi" },
        fields: ["address_components"],
        strictBounds: false,
        types: ["address"],
      };

      set(
        autocomplete,
        new google.maps.places.Autocomplete(osoite.value, options)
      );

      if (store.getters.getAddressLine !== "")
        osoite.value.value = store.getters.getAddressLine;

      enableEnterKey(unrefElement(osoite));
    });

    return { osoite, autocomplete, google };
  },
  data: function () {
    return {
      loading: false,
      error: false,
      productData: null,
      filteredProducts: null,
      simplifiedProducts: null,
      luomu: false,
      postcodeIsValid: null,
      phStart: 6.0,
      phTarget: 6.5,
      area: 10,
      selectedKalkki1: null,
      selectedKalkki2: null,
      selectedKalkki3: null,
      selectedMaalaji: [2, 3, 5, 6],
      selectedMultavuus: 0,
      maalajit: [
        { value: [2, 3, 5, 6], text: "Karkea kivennäismaa" },
        { value: [3, 4, 6, 7], text: "Savinen kivennäismaa" },
        { value: [4, 5, 7, 8], text: "Hietasavi, hiuesavi,hiesusavi" },
        { value: [6, 7, 8, 9], text: "Aitosavi" },
        { value: [10], text: "Multamaa, turvemaa, liejusavi" },
      ],
      multavuudet: [
        { text: "vähämultainen", value: 0 },
        { text: "multava", value: 1 },
        { text: "runsasmultainen", value: 2 },
        { text: "erittäin runsasmultainen", value: 3 },
      ],
    };
  },
  computed: {
    ...mapGetters(["getAddressLine", "getKalkkilaskuriProducts", "getKalkit"]),
    ...mapState(["address_info"]),
  },
  watch: {
    address_info(newValue) {
      const input = get(this.osoite);
      if (newValue.addressLine !== input.value) {
        input.value = newValue.addressLine;
      }
    },
  },
  created: function () {
    this.initializeProducts();
  },
  methods: {
    ...mapMutations({
      setFilteredProducts: SET_FILTERED_PRODUCTS,
      setSelectedAmounts: SET_SELECTED_AMOUNTS,
    }),
    ...mapActions({
      updateProfile: UPDATE_PROFILE,
      fetchProducts: FETCH_PRODUCTS,
    }),
    setSelectedMaalaji(event) {
      this.setSelectedAmounts({
        selectedMaalaji: Object.assign(
          {},
          event.target.value.split(",").map((maa) => parseInt(maa))
        ),
      });
    },
    storeValues: function () {
      let maalaji = "Karkea kivennäismaa";
      this.maalajit.forEach((ml) => {
        if (this.selectedMaalaji == ml.value) {
          maalaji = ml.text;
        }
      });
      let multavuus = "";
      this.multavuudet.forEach((mu) => {
        if (this.selectedMultavuus == mu.value) {
          multavuus = mu.text;
        }
      });

      let valuestring =
        maalaji +
        ", " +
        multavuus +
        ", lähtö pH:" +
        this.phStart +
        ", tavoite pH:" +
        this.phTarget +
        ", lohkon koko:" +
        this.area;
      if (this.selectedKalkki1) {
        valuestring += ", Tuote 1: " + this.selectedKalkki1.title;
      }
      if (this.selectedKalkki2) {
        valuestring += ", Tuote 2: " + this.selectedKalkki2.title;
      }

      let formField = document.getElementById("input_2_12"); // Store value to  Gravity Forms hidden field
      if (formField) {
        // console.debug( formField )
        formField.value = valuestring;
      }

      sessionStorage.setItem("soilfood_laskuri", valuestring);
    },
    initializeProducts: async function () {
      this.loading = true;
      await this.fetchProducts();
      this.loading = false;
    },
    resetFilteredProducts: function () {
      this.filteredProducts = this.productData;
    },
    handleSubmit: async function () {
      this.loading = true;
      this.error = false;
      try {
        const input = get(this.osoite);
        if (input.value.length <= 0) {
          this.loading = false;
          this.error = true;
          return;
        }
        // Send GTM Event only on first search
        if (!this.getKalkkilaskuriProducts) {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: "kalkit_search_trigger",
            form_id: "kalkkilaskuri",
          });
        }

        const location = await retrieveLocation(input.value);

        if (!location) {
          this.loading = false;
          this.error = true;
          return;
        }
        if (input.value) location.placeObject.addressLine = input.value;
        if (location.geometryLocation)
          location.placeObject.geometryLocation = location.geometryLocation;

        // Update the address.
        await this.updateProfile({
          placeObject: location.placeObject,
          ignoreSellerError: true,
        });

        // Filter products
        await this.filterProducts(location);

        this.loading = false;
      } catch (e) {
        this.loading = false;
      }
    },
    filterProducts: async function (location) {
      const { geometryLocation } = location;

      this.postcodeIsValid = true;
      const products = this.getKalkit;

      let filteredProducts = await Promise.all(
        products.map(async (product) => {
          console.debug(product);
          if (!product.acf.geopoint) {
            product.distance = false;
            return product;
          }
          const locArray = product.acf.geopoint.split(",");
          const loc = { lat: locArray[0], lng: locArray[1] };
          const range = parseInt(product.acf.toimitusetaisyys);
          const calculated = isInRange(geometryLocation, loc, range);
          // Only calculate directions for distances over > 100
          console.log("Checking: ", calculated, range / 1.4);
          if (calculated && calculated > range / 1.4) {
            product.distance = await getDirectionDistance(
              geometryLocation,
              loc,
              range
            );
          } else {
            product.distance = calculated;
          }
          console.log(product.distance);
          return product;
        })
      );

      filteredProducts = filteredProducts.filter(
        (prod) => prod.distance !== false
      );

      console.log(filteredProducts);

      // Sort by distance
      filteredProducts.sort((a, b) =>
        a.distance > b.distance ? 1 : b.distance > a.distance ? -1 : 0
      );

      // Reduce by category
      filteredProducts = this.simplifyProducts(filteredProducts);

      this.setFilteredProducts(filteredProducts);

      this.setSelectedAmounts({
        area: parseFloat(this.area),
        phStart: parseFloat(this.phStart),
        phTarget: parseFloat(this.phTarget),
        selectedMaalaji: this.selectedMaalaji,
        selectedMultavuus: this.selectedMultavuus,
      });

      // GA Event
      if (window.dataLayer) {
        window.dataLayer.push({
          event: "formSubmission",
          formID: "zipCode",
          formValue:
            location.placeObject.postal_code ??
            location.placeObject.addressLine,
        });
      }
    },
    simplifyProducts: function (productList) {
      return createProductObject(productList);
    },
  },
};
</script>

<style scoped lang="scss">
.input-narrow {
  width: 200px;
}
.info-text {
  font-size: 11px;
  margin-top: 16px;
  color: var(--bs-dark);
}
span.error-message {
  color: var(--bs-red);
}
.laskuri-container {
  @media screen and (min-width: 768px) {
    padding: 2rem 3rem;
  }

  @media screen and (max-width: 768px) {
    padding: 1rem 1rem;
  }

  background-color: var(--bs-green);

  .laskuri-column {
    display: flex;
    flex-direction: column;
    justify-content: stretch;
    .laskuri-input {
      margin-bottom: 1rem;
    }
    .laskuri-submit-input {
      order: 5;
      height: 100%;
      .btn {
        display: flex;
        justify-content: center;
        align-items: center;
        i {
          margin-right: 0.6rem;
        }
      }
    }

    .laskuri-area-input {
      @media screen and (max-width: 768px) {
        order: 1;
      }
    }
  }

  .show-button {
    margin-top: 2rem;
    display: flex;
    justify-content: flex-end;
    @media screen and (max-width: 768px) {
      justify-content: center;
    }
    button {
      padding: 4px 34px;
      align-self: flex-end;
    }
  }
}

.loading-icon {
  animation: spin 2000ms infinite linear;
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
}
</style>
